import {
  LoadOperation,
  TargetSchema,
  type Claimant,
  type ClaimItem,
  type DocumentLoad,
  type Policy,
  type PolicyData,
  type StoplossClaim,
} from "@/types";
import { getEffectiveDeductible, getResolvedClaimsValues } from "@/resources/claimants/hooks/useCurrentPolicyData";
import type { ParsedLoadWarnings } from "./parseLoadWarnings";

/**
 * Pure model behind the post-load claimants table (#7550). Everything here is
 * derived from the load record and the entities it names, so the table's
 * rules (sort, outcome, filters, footer) are unit-testable without a provider.
 */

export type ClaimantOutcome = "new" | "updated" | "unchanged";

export type ClaimantRowFilter = "all" | "new" | "outside-policy" | "stop-loss" | "attention";

/** A load warning that lands on one claimant's row (held spend, skipped totals). */
export interface RowAttention {
  /** Short row note, e.g. "Spend held · counted next load". */
  text: string;
  /** Full explanation for the hint. */
  detail: string;
}

export interface ClaimantRow {
  claimantId: string;
  claimant: Claimant | undefined;
  name: string;
  relationship: string | undefined;
  memberId: string | undefined;
  birthDate: string | undefined;
  /** This load's claim lines for the claimant, in load order. */
  lines: ClaimItem[];
  /** Sum of paid_amount over `lines`. */
  paidInFile: number;
  /** The claimant's running total after the load (Claimant.total_paid). */
  ytdAfter: number | undefined;
  /** The claimant's running total before the load, when the load recorded it. */
  ytdBefore: number | undefined;
  deductible: number | undefined;
  /** "Specific" / "Lasered" / … from getEffectiveDeductible, or null when none applies. */
  deductibleLabel: string | null;
  outcome: ClaimantOutcome;
  /** Stop-loss claim this load created for the claimant, if any. */
  stoplossClaimOpened: StoplossClaim | undefined;
  /** Open stop-loss claim this load added spend to (the load updated it), if any. */
  stoplossClaimAdded: StoplossClaim | undefined;
  /** The claimant's spend over the deductible after the load, when the record carries it. */
  spendOverDeductible: number | undefined;
  outsidePolicyCount: number;
  /** True when the claimant's line fetch was capped and `lines` may be incomplete. */
  linesPartial: boolean;
  /** A load warning about this claimant, when the load recorded one. */
  attention: RowAttention | undefined;
}

/** Row-level wording for the load's warnings; the long reason text stays in the hint. */
export const buildAttentionMap = (
  parsed: ParsedLoadWarnings,
  reasonText: Record<string, string>
): Record<string, RowAttention> => {
  const map: Record<string, RowAttention> = {};
  for (const h of parsed.heldSpend) {
    const text =
      h.reason === "deductible_review_pending"
        ? "Spend held · deductible review open"
        : h.reason === "concurrent_insert" || h.reason === undefined
          ? "Spend held · counted next load"
          : "Spend held";
    const detail = reasonText[h.reason ?? "legacy"] ?? reasonText.unknown ?? "";
    const prev = map[h.claimantId];
    // Two held warnings for one claimant (a medical and an Rx bucket) keep both.
    map[h.claimantId] = prev
      ? {
          text: prev.text === text ? text : `${prev.text} · ${text}`,
          detail: prev.detail === detail ? detail : `${prev.detail} ${detail}`,
        }
      : { text, detail };
  }
  for (const f of parsed.fenceSkipped) {
    map[f.claimantId] = {
      text: "Totals not updated this load",
      detail: `This load could not fence the claimant within its wait budget${f.waited ? ` (${f.waited})` : ""}, so its totals and stop-loss claims were not updated by this load. The next load or recompute catches up.`,
    };
  }
  return map;
};

/** Reasons the loader stamps on a line that falls outside the policy's windows. */
export const OUTSIDE_POLICY_REASONS: readonly string[] = [
  "paid_date_outside_policy",
  "service_date_outside_incurred_period",
];

export const isOutsidePolicy = (line: Pick<ClaimItem, "ineligible_reasons">): boolean =>
  (line.ineligible_reasons ?? []).some((r) => OUTSIDE_POLICY_REASONS.includes(r));

export const outcomeFor = (operation: LoadOperation | undefined): ClaimantOutcome => {
  switch (operation) {
    case LoadOperation.CREATED:
      return "new";
    case LoadOperation.UPDATED:
      return "updated";
    default:
      return "unchanged";
  }
};

export const claimantDisplayName = (c: Claimant | undefined, fallbackId: string): string =>
  c?.claimant_name ||
  [c?.claimant_first_name, c?.claimant_last_name].filter(Boolean).join(" ").trim() ||
  `Claimant ${fallbackId.slice(0, 8)}`;

/** Ids of a schema in the load, with their operations, in load order. */
export const loadIdsFor = (
  load: Pick<DocumentLoad, "loaded_records">,
  schema: TargetSchema
): { ids: string[]; operations: Record<string, LoadOperation> } => {
  const ids: string[] = [];
  const operations: Record<string, LoadOperation> = {};
  for (const r of load.loaded_records ?? []) {
    if (r.target_schema !== schema) continue;
    if (!(r.entity_id_ref in operations)) ids.push(r.entity_id_ref);
    operations[r.entity_id_ref] = r.operation;
  }
  return { ids, operations };
};

/**
 * The load's claim items, per claimant. `claimItems` may include lines from
 * earlier loads (they come from a per-claimant list request); only the ids the
 * load names count.
 */
/** Clock skew allowed between the load record's timestamp and a claim it created. */
export const STOPLOSS_CREATED_SKEW_MS = 2 * 60 * 1000;

/**
 * A stop-loss claim fetched for this document (its `source_documents` names
 * the document) was either opened by this load or already open and added to.
 * Without a load timestamp nothing can be said, so such a claim is left out.
 */
export const classifyStoplossClaimByTime = (
  claim: Pick<StoplossClaim, "created_datetime">,
  loadCreatedAt: string | undefined
): LoadOperation | undefined => {
  if (!loadCreatedAt || !claim.created_datetime) return undefined;
  const created = Date.parse(String(claim.created_datetime));
  const loaded = Date.parse(loadCreatedAt);
  if (!Number.isFinite(created) || !Number.isFinite(loaded)) return undefined;
  return created >= loaded - STOPLOSS_CREATED_SKEW_MS ? LoadOperation.CREATED : LoadOperation.UPDATED;
};

/**
 * Which of the claimant's policy_data entries this load speaks to: the policy
 * its lines carry (most common policy_id_ref), else the only entry, else the
 * caller's fallback (the client's newest policy). Exported for tests.
 */
export const pickPolicyDataKey = (
  claimant: Claimant | undefined,
  lines: Pick<ClaimItem, "policy_id_ref">[],
  fallbackPolicyId: string | undefined
): string | undefined => {
  const keys = Object.keys(claimant?.policy_data ?? {});
  const tally = new Map<string, number>();
  for (const l of lines) {
    const k = l.policy_id_ref ? String(l.policy_id_ref) : null;
    if (k) tally.set(k, (tally.get(k) ?? 0) + 1);
  }
  const fromLines = [...tally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  if (fromLines && (keys.length === 0 || keys.includes(fromLines))) return fromLines;
  if (keys.length === 1) return keys[0];
  if (fallbackPolicyId && keys.includes(fallbackPolicyId)) return fallbackPolicyId;
  return keys[0];
};

/**
 * The policy this load ran against, from the load itself: the term most of this
 * load's lines carry (`policy_id_ref`), looked up in the client's policies. A
 * run-out load after renewal names the old term this way, where "the client's
 * newest policy" would name the new one (!4974 thread 31129). Falls back to the
 * newest policy only when no line names a term. The load record's own
 * `policy_id_ref` (coronis-apps !4977, AB#7556) supersedes this once read.
 */
export const resolveLoadPolicy = (
  load: Pick<DocumentLoad, "loaded_records">,
  claimItems: Pick<ClaimItem, "id" | "policy_id_ref">[],
  policies: Policy[]
): Policy | undefined => {
  const lineIds = new Set(loadIdsFor(load, TargetSchema.CLAIM_ITEM).ids);
  const tally = new Map<string, number>();
  for (const l of claimItems) {
    if (!lineIds.has(String(l.id)) || !l.policy_id_ref) continue;
    const k = String(l.policy_id_ref);
    tally.set(k, (tally.get(k) ?? 0) + 1);
  }
  const fromLines = [...tally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const named = fromLines ? policies.find((p) => String(p.id) === fromLines) : undefined;
  return named ?? policies[0];
};

export const buildClaimantRows = ({
  load,
  claimants,
  claimItems,
  stoplossClaims,
  policy,
  partialClaimantIds = new Set<string>(),
  attention = {},
  loadCreatedAt,
}: {
  load: Pick<DocumentLoad, "loaded_records">;
  claimants: Claimant[];
  claimItems: ClaimItem[];
  stoplossClaims: StoplossClaim[];
  /** The client's policy for this load, when known: deductible base and policy_data fallback key. */
  policy?: Policy;
  /** Claimants whose line fetch hit the server's page ceiling. */
  partialClaimantIds?: Set<string>;
  /** Row-level load warnings by claimant id (see buildAttentionMap). */
  attention?: Record<string, RowAttention>;
  /**
   * When the load ran. The load record does not list the stop-loss claims it
   * opened or added to (the loader writes them in post-processing), so claims
   * fetched by `document_id_ref` are classified by time: created at or after
   * the load → opened by it; older → added to by it.
   */
  loadCreatedAt?: string;
}): ClaimantRow[] => {
  const claimantSet = loadIdsFor(load, TargetSchema.CLAIMANT);
  const lineSet = loadIdsFor(load, TargetSchema.CLAIM_ITEM);
  const lineIds = new Set(lineSet.ids);
  const slSet = loadIdsFor(load, TargetSchema.STOPLOSS_CLAIM);

  const claimantById = new Map(claimants.map((c) => [String(c.id), c]));
  const linesByClaimant = new Map<string, ClaimItem[]>();
  for (const item of claimItems) {
    if (!lineIds.has(String(item.id))) continue;
    const key = String(item.claimant_id_ref ?? "");
    const list = linesByClaimant.get(key) ?? [];
    list.push(item);
    linesByClaimant.set(key, list);
  }
  const openedByClaimant = new Map<string, StoplossClaim>();
  const addedByClaimant = new Map<string, StoplossClaim>();
  for (const sl of stoplossClaims) {
    const key = String(sl.claimant_id_ref ?? "");
    const op = slSet.operations[String(sl.id)] ?? classifyStoplossClaimByTime(sl, loadCreatedAt);
    if (op === LoadOperation.CREATED && !openedByClaimant.has(key)) openedByClaimant.set(key, sl);
    if (op === LoadOperation.UPDATED && !addedByClaimant.has(key)) addedByClaimant.set(key, sl);
  }

  // Claimants the load names, plus any claimant that only appears through its lines.
  const orderedIds = [...claimantSet.ids];
  for (const key of linesByClaimant.keys()) if (key && !claimantSet.operations[key]) orderedIds.push(key);

  const rows = orderedIds.map<ClaimantRow>((claimantId) => {
    const claimant = claimantById.get(claimantId);
    const lines = linesByClaimant.get(claimantId) ?? [];
    const policyKey = pickPolicyDataKey(claimant, lines, policy ? String(policy.id) : undefined);
    const policyData: PolicyData | undefined = policyKey ? claimant?.policy_data?.[policyKey] : undefined;
    const resolved = getEffectiveDeductible(policyData, policy);
    const deductible = resolved.amount > 0 ? resolved.amount : undefined;
    return {
      claimantId,
      claimant,
      name: claimantDisplayName(claimant, claimantId),
      relationship: claimant?.claimant_relationship ? String(claimant.claimant_relationship) : undefined,
      memberId: claimant?.member_id || claimant?.rx_member_id || undefined,
      birthDate: claimant?.claimant_birth_date || undefined,
      lines,
      paidInFile: lines.reduce((sum, l) => sum + (Number(l.paid_amount) || 0), 0),
      ytdAfter: numberOrUndefined(getResolvedClaimsValues(policyData)?.total_paid),
      ytdBefore: undefined,
      deductible,
      deductibleLabel: deductible !== undefined ? resolved.label : null,
      outcome: outcomeFor(claimantSet.operations[claimantId]),
      stoplossClaimOpened: openedByClaimant.get(claimantId),
      stoplossClaimAdded: openedByClaimant.has(claimantId) ? undefined : addedByClaimant.get(claimantId),
      spendOverDeductible: numberOrUndefined(getResolvedClaimsValues(policyData)?.spend_over_deductible),
      outsidePolicyCount: lines.filter(isOutsidePolicy).length,
      linesPartial: partialClaimantIds.has(claimantId),
      attention: attention[claimantId],
    };
  });

  // New claimants first, then by name — the reviewer reads what the load added before what it touched.
  const rank: Record<ClaimantOutcome, number> = { new: 0, updated: 1, unchanged: 2 };
  return rows.sort((a, b) => rank[a.outcome] - rank[b.outcome] || a.name.localeCompare(b.name));
};

const numberOrUndefined = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

export const filterRows = (rows: ClaimantRow[], filter: ClaimantRowFilter): ClaimantRow[] => {
  switch (filter) {
    case "new":
      return rows.filter((r) => r.outcome === "new");
    case "outside-policy":
      return rows.filter((r) => r.outsidePolicyCount > 0);
    case "stop-loss":
      return rows.filter((r) => !!r.stoplossClaimOpened || !!r.stoplossClaimAdded);
    case "attention":
      return rows.filter((r) => !!r.attention);
    default:
      return rows;
  }
};

export const filterCounts = (rows: ClaimantRow[]): Record<ClaimantRowFilter, number> => ({
  all: rows.length,
  new: filterRows(rows, "new").length,
  "outside-policy": filterRows(rows, "outside-policy").length,
  "stop-loss": filterRows(rows, "stop-loss").length,
  attention: filterRows(rows, "attention").length,
});

/** Footer figures over the rows in the current filter (every page, not the visible one). */
export const footerFor = (rows: ClaimantRow[]): { claimants: number; lines: number; paidInFile: number } => ({
  claimants: rows.length,
  lines: rows.reduce((n, r) => n + r.lines.length, 0),
  paidInFile: rows.reduce((sum, r) => sum + r.paidInFile, 0),
});

/**
 * The one note under the Outcome (#7550 AC5), in the review grid's words: a
 * stop-loss row says how far over the deductible the claimant is; any other
 * row says how many of its lines fell outside the policy; a quiet row says
 * nothing.
 */
export const outcomeNote = (row: ClaimantRow, formatUSD: (n: number) => string): string | null => {
  // A warning about the row outranks the routine notes: it is the exception.
  if (row.attention) return row.attention.text;
  if (row.stoplossClaimOpened || row.stoplossClaimAdded) {
    return row.spendOverDeductible !== undefined && row.spendOverDeductible > 0
      ? `${formatUSD(row.spendOverDeductible)} over deductible`
      : null;
  }
  if (row.outsidePolicyCount > 0) {
    return `${row.outsidePolicyCount} ${row.outsidePolicyCount === 1 ? "line" : "lines"} outside policy`;
  }
  return null;
};

/** Outcome label, mirroring the review's On approve column. */
export type OutcomeDisplay =
  | { kind: "pill"; label: "Created SLC" | "Added to SLC"; filled: boolean }
  | { kind: "text"; label: "Created claimant" | "Updated claimant" | "No updates"; bold: boolean };

export const outcomeDisplay = (row: ClaimantRow): OutcomeDisplay => {
  if (row.stoplossClaimOpened) return { kind: "pill", label: "Created SLC", filled: true };
  if (row.stoplossClaimAdded) return { kind: "pill", label: "Added to SLC", filled: false };
  if (row.outcome === "new") return { kind: "text", label: "Created claimant", bold: true };
  if (row.outcome === "updated") return { kind: "text", label: "Updated claimant", bold: false };
  return { kind: "text", label: "No updates", bold: false };
};
